/**
 * Parse Shopify product tags into display badges.
 *
 * Convention: BADGE_<COLOR>_<LABEL>
 * Example: BADGE_RED_HOT-SELLING → { color: "RED", colorClass: "badge-color-red", label: "Hot Selling" }
 *
 * Also supports labels that include hyphens before the color token:
 * BADGE_HOT-SELLING_DEFAULT → { color: "DEFAULT", colorClass: "badge-color-default", label: "Hot Selling" }
 */

export const SUPPORTED_BADGE_COLORS = [
  "DEFAULT",
  "RED",
  "GREEN",
  "BLUE",
  "ORANGE",
  "YELLOW",
  "PURPLE",
  "PINK",
  "BLACK",
  "COPPER",
];

/**
 * @param {string} color
 * @returns {string} CSS utility class, e.g. "badge-color-red"
 */
export const getBadgeColorClass = (color) => {
  const normalized = String(color || "DEFAULT").toUpperCase();
  const safeColor = SUPPORTED_BADGE_COLORS.includes(normalized)
    ? normalized
    : "DEFAULT";
  return `badge-color-${safeColor.toLowerCase()}`;
};

const toTitleCase = (value) =>
  value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * @param {string} tag
 * @returns {{ color: string, colorClass: string, label: string, rawTag: string } | null}
 */
export const parseBadgeTag = (tag) => {
  if (typeof tag !== "string" || !tag.startsWith("BADGE_")) {
    return null;
  }

  const parts = tag.split("_");
  // BADGE + COLOR + at least one LABEL token
  if (parts.length < 3 || parts[0] !== "BADGE") {
    return null;
  }

  let color;
  let labelParts;

  // If the second token contains a hyphen it is part of the label
  // (e.g. BADGE_HOT-SELLING_DEFAULT), so the color is the last token.
  if (parts[1].includes("-")) {
    color = parts[parts.length - 1]?.trim();
    labelParts = parts.slice(1, -1);
  } else {
    color = parts[1]?.trim();
    labelParts = parts.slice(2);
  }

  const labelRaw = labelParts.join("_").replace(/-/g, " ").trim();

  if (!color || !labelRaw) {
    return null;
  }

  const normalized = color.toUpperCase();
  const safeColor = SUPPORTED_BADGE_COLORS.includes(normalized)
    ? normalized
    : "DEFAULT";

  return {
    color: safeColor,
    colorClass: getBadgeColorClass(safeColor),
    label: toTitleCase(labelRaw),
    rawTag: tag,
  };
};

/**
 * @param {string[] | undefined | null} tags
 * @returns {Array<{ color: string, colorClass: string, label: string, rawTag: string }>}
 */
export const parseProductBadges = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) {
    return [];
  }

  return tags.map(parseBadgeTag).filter(Boolean);
};
